import type { Strategy } from '../../voice-to-text/configureParams';
import { strategySchema } from '../../voice-to-text/configureParams';
import type { SchemaOf } from '../../helpers';
import { Joi, makeAllOptional } from '../../helpers';
import deepmerge from 'deepmerge';
import type { MergeHandlerMap } from '../configMerge';

export interface IConfig {
  commonPhrases: string[];
  minimumConfidenceResultWaitTimeoutMillis: number;
  speechContinuationWaitTimeoutMillis: number;
  gspeechFastThreshold: number;
  snipsFastThreshold: number;
  gspeechMinimumThreshold: number;
  snipsMinimumThreshold: number;
  initialPhraseStrategy: Strategy;
  defaultStrategy: Strategy;
  strategyByQuestionId: { [key: string]: Strategy | undefined };
}

export const schemaMapDefault: SchemaOf<IConfig> = {
  commonPhrases: Joi.array().items(Joi.string().not('')).required(),
  minimumConfidenceResultWaitTimeoutMillis: Joi.number().required(),
  speechContinuationWaitTimeoutMillis: Joi.number().required(),
  gspeechFastThreshold: Joi.number().required(),
  snipsFastThreshold: Joi.number().required(),
  gspeechMinimumThreshold: Joi.number().required(),
  snipsMinimumThreshold: Joi.number().required(),
  initialPhraseStrategy: strategySchema.required(),
  defaultStrategy: strategySchema.required(),
  strategyByQuestionId: Joi.object({})
    .pattern(Joi.string(), strategySchema)
    .required(),
};

export const schemaMap = makeAllOptional(schemaMapDefault);

const customMergeMap: Partial<MergeHandlerMap<IConfig>> = {
  commonPhrases: (target, source) => [
    ...new Set([...(target ?? []), ...(source ?? [])]),
  ],
};

export const mergeConfig = (
  target?: Partial<IConfig>,
  source?: Partial<IConfig>
): Partial<IConfig> => {
  if (!target && !source) {
    return {};
  }

  return deepmerge.all<Partial<IConfig>>([{}, target || {}, source || {}], {
    customMerge: (key: keyof IConfig) => customMergeMap[key],
  });
};

const commonPhrases = [
  'meeting',
  'a meeting',
  'for a meeting',
  "it's a meeting",
  'delivery',
  'a delivery',
  'for a delivery',
  'neither',
  'none',
  'Courier',
  'Startrack',
  'Fedex',
  'Australia Post',
  'DHL',
  'Mail',
  'Package',
  'Parcel',
  'Post delivery',
  'Parcel Delivery',
  'Parcel Drop off',
  'check-in',
  'appointment',
];

export const defaultConfig: IConfig = {
  commonPhrases,
  minimumConfidenceResultWaitTimeoutMillis: 750,
  speechContinuationWaitTimeoutMillis: 300,
  gspeechFastThreshold: 0.75,
  snipsFastThreshold: 0.75,
  gspeechMinimumThreshold: 0.51,
  snipsMinimumThreshold: 0.51,
  initialPhraseStrategy: 'fastest-with-threshold',
  defaultStrategy: 'broad-vocabulary',
  strategyByQuestionId: {
    askSignatureRequired: 'fastest-with-threshold',
    confirmMeetingVisitor: 'fastest-with-threshold',
    askVisitorName: 'broad-vocabulary',
    askStaffName: 'broad-vocabulary',
    askSleepRequired: 'fastest-with-threshold',
  },
};
